import { useCallback, useEffect, useMemo, useState } from 'react';
import { logbookApiService } from '../services/logbookApi';
import { serverListApiService, type ServerListEntry } from '../services/serverListApi';
import type { LogbookServer, LogbookSyncResult } from '../types/logbook';

/**
 * The Logbook server inventory, joined to the PlayCDU pack list.
 *
 * A Logbook `server_id` is the server address of one of our packs, which is the hinge that turns a
 * bare identifier into something a moderator recognises: the pack it runs, its cluster, its region
 * and whether it is up right now. The join is on the pack list's `serverAddress` and on the
 * addresses of its cluster children, case-insensitively.
 *
 * The join is best effort in both directions. A retired server stays in Logbook configuration so
 * its history remains queryable and can outlive its pack-list entry, and a pack can exist with no
 * evidence yet. Neither case hides the other: unmatched Logbook servers keep their raw id.
 */

export interface LogbookServerView {
  serverId: string;
  /** Cluster as Logbook has it configured, falling back to the pack list. */
  cluster: string | null;
  /** Operator metadata from Logbook, e.g. `status: "archived"` on a retired server. */
  metadata: LogbookServer;
  archived: boolean;
  pack: {
    title: string;
    version: string | null;
    mpiId: number | null;
    thumbnailUrl: string | null;
    minecraftVersion: string | null;
    region: string | null;
    online: boolean;
    players: number;
    maxPlayers: number;
  } | null;
  /** Last insertion receipt from `/sync`, or null when nothing has arrived yet. */
  lastReceivedAt: number | null;
}

export interface UseLogbookServersResult {
  servers: LogbookServerView[];
  byId: Map<string, LogbookServerView>;
  clusters: string[];
  loading: boolean;
  error: string | null;
  /** Producer backlog wording from `/sync`; it always warns that local backlog may exist. */
  syncSemantics: { backlog: string; semantics: string } | null;
  reload: () => void;
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

type PackMatch = { entry: ServerListEntry; child?: NonNullable<ServerListEntry['clusterChildren']>[number] };

function indexPackList(entries: ServerListEntry[]): Map<string, PackMatch> {
  const index = new Map<string, PackMatch>();
  for (const entry of entries) {
    if (entry.serverAddress) index.set(normalize(entry.serverAddress), { entry });
    for (const child of entry.clusterChildren ?? []) {
      if (child.serverAddress) index.set(normalize(child.serverAddress), { entry, child });
    }
  }
  return index;
}

export function useLogbookServers(): UseLogbookServersResult {
  const [servers, setServers] = useState<LogbookServerView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncSemantics, setSyncSemantics] = useState<{ backlog: string; semantics: string } | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);

    const run = async () => {
      try {
        // The pack list and the sync receipts are enrichment: neither failing should hide the
        // servers Logbook itself reported, so only the inventory read can fail the hook.
        const inventory = await logbookApiService.getServers(controller.signal);
        const [packs, sync] = await Promise.all([
          serverListApiService.getServerList().catch(() => [] as ServerListEntry[]),
          logbookApiService.getSync(controller.signal).catch(() => null as LogbookSyncResult | null),
        ]);
        if (cancelled) return;

        const packIndex = indexPackList(packs);
        const receipts = new Map((sync?.servers ?? []).map((entry) => [entry.server_id, entry.last_received_at]));
        const rows = (Array.isArray(inventory?.servers) ? inventory.servers : []) as LogbookServer[];

        const views: LogbookServerView[] = rows.map((metadata) => {
          const serverId = String(metadata.server_id ?? '');
          const match = packIndex.get(normalize(serverId));
          const entry = match?.entry;
          const child = match?.child;
          return {
            serverId,
            cluster: metadata.cluster ?? entry?.clusterName ?? null,
            metadata,
            archived: normalize(metadata.status ?? '') === 'archived',
            pack: entry
              ? {
                title: entry.modpackTitle,
                version: entry.modpackVersion ?? null,
                mpiId: entry.modpackIndexId ?? null,
                thumbnailUrl: entry.thumbnailUrl ?? null,
                minecraftVersion: entry.minecraftVersion ?? null,
                region: child?.regionName ?? entry.regionName ?? null,
                online: child ? child.isServerOnline : entry.isServerOnline,
                players: child ? child.onlinePlayerCount : entry.onlinePlayerCount,
                maxPlayers: child ? child.maxPlayerCount : entry.maxPlayerCount,
              }
              : null,
            lastReceivedAt: receipts.get(serverId) ?? null,
          };
        }).sort((a, b) => a.serverId.localeCompare(b.serverId));

        setServers(views);
        setSyncSemantics(sync ? { backlog: sync.producer_backlog, semantics: sync.semantics } : null);
        setError(null);
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setServers([]);
        setError(caught instanceof Error ? caught.message : 'The Logbook server list could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => { cancelled = true; controller.abort(); };
  }, [nonce]);

  const byId = useMemo(() => new Map(servers.map((server) => [server.serverId, server])), [servers]);
  const clusters = useMemo(
    () => Array.from(new Set(servers.map((server) => server.cluster).filter((value): value is string => Boolean(value)))).sort(),
    [servers],
  );
  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { servers, byId, clusters, loading, error, syncSemantics, reload };
}
